import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { trackLegalPageViewed } from "@/lib/analytics";

const Terms = () => {
  const navigate = useNavigate();

  useEffect(() => {
    trackLegalPageViewed('terms');
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button onClick={() => navigate(-1)} variant="ghost" className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card className="p-8">
          <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
          <p className="text-muted-foreground mb-8">Last updated: {new Date().toLocaleDateString()}</p>

          <div className="space-y-8 text-sm leading-relaxed">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Agreement to Terms</h2>
              <p className="text-muted-foreground">
                By accessing or using Decor Canvas - Interior Decor AI Designer ("Service," "we," "our," or "us"), a SaaS provided by 
                <strong> Athenia AI Labs Private Limited</strong>, you agree to be bound by these Terms of Service. 
                If you do not agree to these terms, you may not access or use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Service Description</h2>
              <p className="text-muted-foreground mb-3">
                Decor Canvas - Interior Decor AI Designer is an AI-powered interior design visualization platform that allows users to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>Upload room photos or floor plans</li>
                <li>Generate AI-powered design variations in multiple styles</li>
                <li>Save, compare, and download design projects</li>
                <li>Access design tools and features based on subscription tier</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. User Accounts</h2>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">3.1 Account Creation</h3>
              <p className="text-muted-foreground mb-3">
                To use the Service, you must:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>Be at least 13 years of age</li>
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">3.2 Account Termination</h3>
              <p className="text-muted-foreground">
                We reserve the right to suspend or terminate accounts that violate these terms, engage in fraudulent activity, 
                or abuse the Service. You may delete your account at any time through Account Settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Subscription Plans and Billing</h2>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">4.1 Free Tier</h3>
              <p className="text-muted-foreground">
                The free tier includes 3 one-time trial credits. Free tier credits do not reset monthly.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">4.2 Paid Subscriptions</h3>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>Subscriptions are billed monthly or annually in advance</li>
                <li>Prices are displayed in your local currency when available</li>
                <li>Payment is processed securely through Dodo Payments (Web/Android) or Apple In-App Purchase (iOS)</li>
                <li>Subscriptions auto-renew unless cancelled before the renewal date</li>
                <li>All fees are non-refundable except as required by law</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">4.3 Cancellation</h3>
              <p className="text-muted-foreground">
                You may cancel your subscription at any time through Account Settings. Cancellation takes effect at the end of your current billing period. 
                You will retain access to paid features until the end of the paid period.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">4.4 Refund Policy</h3>
              <p className="text-muted-foreground">
                We generally do not provide refunds for unused portions of subscriptions. However, we may provide refunds on a case-by-case basis 
                for billing errors or technical issues that prevent service use. Contact hello.dc@atheniaai.com within 7 days of charge.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Acceptable Use</h2>
              <p className="text-muted-foreground mb-3">You agree NOT to:</p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>Upload content that violates laws, infringes rights, or contains illegal material</li>
                <li>Use the Service for any unlawful or fraudulent purpose</li>
                <li>Attempt to reverse engineer, decompile, or extract our AI models</li>
                <li>Scrape, crawl, or use automated tools to access the Service</li>
                <li>Share or resell access to your account</li>
                <li>Upload viruses, malware, or malicious code</li>
                <li>Impersonate others or provide false information</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Use the Service to create content that is hateful, discriminatory, or harassing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Intellectual Property Rights</h2>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">6.1 Your Content</h3>
              <p className="text-muted-foreground">
                You retain ownership of images and content you upload. By uploading content, you grant us a limited license to process, 
                store, and display your content solely to provide the Service. We do not claim ownership of your designs.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">6.2 Generated Designs</h3>
              <p className="text-muted-foreground">
                AI-generated designs are provided to you for your personal or commercial use. However, because AI generation may produce 
                similar results for different users, we cannot guarantee exclusive rights to any specific AI-generated output.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">6.3 Our Platform</h3>
              <p className="text-muted-foreground">
                The Service, including our AI models, algorithms, software, design, and trademarks, is protected by intellectual property laws. 
                You may not copy, modify, distribute, or create derivative works without our written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Service Availability and Limitations</h2>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">7.1 Availability</h3>
              <p className="text-muted-foreground">
                We strive for 99%+ uptime but do not guarantee uninterrupted service. We may temporarily suspend the Service for maintenance, 
                updates, or unforeseen technical issues.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">7.2 AI Limitations</h3>
              <p className="text-muted-foreground mb-3">
                AI-generated designs are provided "as is." We do not guarantee:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>Accuracy or realism of generated designs</li>
                <li>Structural feasibility or building code compliance</li>
                <li>Suitability for actual construction or renovation</li>
                <li>Professional architectural or design standards</li>
              </ul>
              <p className="text-muted-foreground mt-3">
                <strong>Generated designs are for visualization purposes only and should not replace professional architectural or design services.</strong>
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">7.3 Data Storage</h3>
              <p className="text-muted-foreground">
                We implement regular backups, but we are not responsible for data loss due to technical failures, account deletion, or service termination. 
                You should maintain your own backups of important designs.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Disclaimers and Warranties</h2>
              <div className="bg-muted/30 p-4 rounded-lg">
                <p className="text-muted-foreground font-semibold mb-2">THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.</p>
                <p className="text-muted-foreground">
                  We disclaim all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, 
                  and non-infringement. We do not warrant that the Service will be uninterrupted, secure, or error-free.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Limitation of Liability</h2>
              <div className="bg-muted/30 p-4 rounded-lg">
                <p className="text-muted-foreground mb-2">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>Indirect, incidental, special, consequential, or punitive damages</li>
                  <li>Loss of profits, revenue, data, or use</li>
                  <li>Damages resulting from your use or inability to use the Service</li>
                  <li>Damages from reliance on AI-generated designs</li>
                </ul>
                <p className="text-muted-foreground mt-3">
                  Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim, or $100, whichever is greater.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Indemnification</h2>
              <p className="text-muted-foreground">
                You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from your use of the Service, 
                your violation of these terms, or your violation of any rights of others.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Dispute Resolution</h2>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">11.1 Governing Law</h3>
              <p className="text-muted-foreground">
                These terms are governed by the laws of [Your Jurisdiction], without regard to conflict of law principles.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">11.2 Arbitration</h3>
              <p className="text-muted-foreground">
                Any disputes shall be resolved through binding arbitration in accordance with [Arbitration Rules]. 
                You waive the right to participate in class action lawsuits or class-wide arbitration.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">11.3 Exceptions</h3>
              <p className="text-muted-foreground">
                Either party may seek injunctive relief in court for intellectual property infringement or unauthorized access.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Marketing Communications</h2>
              <p className="text-muted-foreground">
                By creating an account, you are opted in to receive product updates, feature announcements, and
                promotional emails from Decor Canvas by default. This applies to all sign-up methods, including
                email, Google, and Apple sign-in, on both web and mobile. You can opt out of marketing
                communications at any time from <strong>Account Settings → Marketing Emails</strong>, or by using
                the unsubscribe link included in any marketing email. Service and account-related messages
                (such as payment confirmations and security notices) are not affected by this setting.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">13. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We may modify these terms at any time. Material changes will be notified via email or prominent notice on the Service. 
                Your continued use after changes indicates acceptance. If you disagree with changes, you must stop using the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">14. Termination</h2>
              <p className="text-muted-foreground">
                We may terminate or suspend your access immediately for violations of these terms. Upon termination, 
                your right to use the Service ceases immediately. Provisions regarding liability, warranties, and dispute resolution survive termination.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">15. General Provisions</h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li><strong>Entire Agreement:</strong> These terms constitute the entire agreement between you and us</li>
                <li><strong>Severability:</strong> If any provision is invalid, the remaining provisions remain in effect</li>
                <li><strong>No Waiver:</strong> Our failure to enforce any right does not waive that right</li>
                <li><strong>Assignment:</strong> You may not assign these terms; we may assign without restriction</li>
                <li><strong>Force Majeure:</strong> We are not liable for delays caused by circumstances beyond our control</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">16. Contact Information</h2>
              <p className="text-muted-foreground mb-3">
                For questions about these terms, please contact:
              </p>
              <div className="bg-muted/30 p-4 rounded-lg">
                <p className="font-semibold">Athenia AI Labs Private Limited</p>
                <p>Email: hello.dc@atheniaai.com</p>
              </div>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Terms;
